import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Layout/Navbar';
import './PricingPage.css';

const PACKAGES = [
  {
    key: 'operator',
    name: 'Operator',
    subtitle: "You're getting organized.",
    plusBar: null,
    features: [
      'CRM & pipeline visibility',
      'Digital contracts & forms',
      'Social media & marketing suite',
      'Lead nurturing & follow-up suite',
      'Storm & lead tracking',
      'Financial operating system',
      'Discounted merchant services',
      'Weekly support workshops',
      'QuickBooks connection (CRM)',
      'SEO audit',
    ],
    rapidReports: '100',
    preciseReports: '$39.97 EACH',
    featured: false,
  },
  {
    key: 'command',
    name: 'Command Center',
    subtitle: "You're running your business.",
    plusBar: 'Everything in the Operator, plus:',
    features: [
      'Job material ordering',
      'Permit history',
      'QuickBooks full reconciliation',
      'Access to merchant funding',
      'Weather alert system',
      'NWS-verified storm swaths',
      'Field sales platform',
      'Drip campaign management',
      'Account management team access',
      'EBITDA calculation & tracking',
      'Payroll package',
    ],
    rapidReports: '1,000',
    preciseReports: '$39.97 EACH',
    featured: true,
  },
  {
    key: 'high-command',
    name: 'High Command',
    subtitle: "You're operating ahead of the market.",
    plusBar: 'Everything in Command Center, plus:',
    features: [
      'Sales forecasting',
      'Strategic automations library',
      'Full team training & onboarding',
      'Dedicated account manager',
      'Customer website',
      'SEO implementation',
    ],
    rapidReports: 'UNLIMITED',
    preciseReports: '$39.97 EACH',
    featured: false,
  },
];

const NOT_A_FIT = [
  'One-crew shops looking for a simple job tracker',
  "Price-first contractors who won't invest in systems",
  'Businesses run entirely from a cell phone and gut feel',
  'Operators who resist process and prefer to improvise',
  'Shops with no interest in measuring margin or performance',
];

const BUILT_FOR = [
  'Owner-operators running $1M–$10M who want real control',
  'Operations managers drowning in disconnected tools',
  'Sales reps who want to be first to the storm lead',
  'Companies that are scaling and need a system that scales with them',
  'Shops that know chaos is costing them money and are ready to fix it',
];

const REAL_DIFFERENCE = [
  {
    title: 'Storm-to-check coverage.',
    body: 'No other tool runs the full revenue cycle in one system.',
  },
  {
    title: 'Built for roofing, not adapted for it.',
    body:
      'No configuration. No workarounds. No trying to make a generic CRM think like a roofer.',
  },
  {
    title: 'One price. No surprises.',
    body: 'Not per-user. Not tiered. One flat rate for the whole shop.',
  },
  {
    title: 'Operator-first design.',
    body:
      'Built for the owner who runs the business, not the IT buyer who evaluates software.',
  },
];

const TOAST_NOTIFICATIONS = [
  { title: 'NEXRAD Hail Event', desc: 'Tarrant County hail swath logged: 1.8 in hail.' },
  {
    title: 'Margin Block Guarded Bid',
    desc: 'Proposal at 104 Oak Dr locked: profit margin below 22% constraint.',
  },
  {
    title: 'Automatic Payment Scheduled',
    desc: 'Invoice reminder sent for Job #2192 (Hendricks Roof).',
  },
];

const PricingPage = () => {
  const [toast, setToast] = useState(null);
  const timersRef = useRef([]);

  useEffect(() => {
    window.scrollTo(0, 0);

    const prevTitle = document.title;
    document.title = 'Packages | RoofingAssist.AI';

    const setMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      const prev = el.getAttribute('content');
      el.setAttribute('content', content);
      return () => {
        if (created) {
          el.remove();
        } else if (prev !== null) {
          el.setAttribute('content', prev);
        }
      };
    };

    const restores = [
      setMeta(
        'name',
        'description',
        'Discover the transparent, ROI-driven pricing packages for RoofingAssist.AI. Find the perfect scale for your roofing business.'
      ),
      setMeta('property', 'og:title', 'Packages | RoofingAssist.AI'),
      setMeta(
        'property',
        'og:description',
        'Three packages. One operating system. Compare Operator, Command Center, and High Command.'
      ),
    ];

    return () => {
      document.title = prevTitle;
      restores.forEach((restore) => restore());
    };
  }, []);

  // Live-system notification demo (matches mockup storm-alert simulation)
  useEffect(() => {
    const timers = timersRef.current;
    let index = 0;

    const showNext = () => {
      setToast(TOAST_NOTIFICATIONS[index]);
      index = (index + 1) % TOAST_NOTIFICATIONS.length;
      timers.push(setTimeout(() => setToast(null), 4000));
      timers.push(setTimeout(showNext, 22000 + Math.random() * 8000));
    };

    timers.push(setTimeout(showNext, 7000));

    return () => {
      timers.forEach(clearTimeout);
      timers.length = 0;
    };
  }, []);

  return (
    <div className="ra-pricing">
      <Navbar theme="light" />

      {/* HONEST ABOUT FIT */}
      <section className="pr-fit-section">
        <div className="pr-container">
          <div className="pr-fit-grid">
            <div className="pr-fit-left">
              <div className="pr-fit-eyebrow orange">Honest About Fit</div>
              <div className="pr-fit-underline" aria-hidden="true"></div>
              <h2 className="pr-fit-title">We're not for everyone. That's intentional.</h2>
              <p className="pr-fit-subtext">
                We built this for operators who want to run a system &mdash; not
                manage software. If that's not you yet, this probably isn't the
                right move.
              </p>
            </div>

            <div className="pr-fit-column">
              <div className="pr-fit-eyebrow gray">Not a Fit</div>
              <ul className="pr-fit-list">
                {NOT_A_FIT.map((item) => (
                  <li className="pr-fit-item" key={item}>
                    <span className="pr-fit-icon cross" aria-hidden="true">&times;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pr-fit-column">
              <div className="pr-fit-eyebrow orange">Built For</div>
              <ul className="pr-fit-list">
                {BUILT_FOR.map((item) => (
                  <li className="pr-fit-item" key={item}>
                    <span className="pr-fit-icon check" aria-hidden="true">&#10003;</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* PACKAGES */}
      <section className="pr-packages-section" id="packages">
        <div className="pr-container">
          <div className="pr-section-header">
            <div className="pr-eyebrow">Packages</div>
            <h2>Invest in Your Growth</h2>
            <p>
              Select the territory scope and features that match your operation.
              <br />
              <strong>No hidden seat fees.</strong>
            </p>
          </div>

          <div className="pr-cards-grid">
            {PACKAGES.map((pkg) => (
              <Link
                key={pkg.key}
                to="/book"
                className={`pr-price-card${pkg.featured ? ' featured' : ''}`}
              >
                {pkg.featured && (
                  <div className="pr-featured-badge">Most Operators Choose This</div>
                )}

                <div className="pr-card-header">
                  <h3>{pkg.name}</h3>
                </div>

                <div className="pr-card-body">
                  <div className="pr-card-subtitle">{pkg.subtitle}</div>

                  <div className="pr-card-features">
                    {pkg.plusBar && <div className="pr-plus-bar">{pkg.plusBar}</div>}
                    <ul className="pr-features-list">
                      {pkg.features.map((feature) => (
                        <li key={feature}>&mdash; {feature}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="pr-card-footer">
                  <div className="pr-footer-row">
                    <span className="pr-footer-label">Rapid Reports</span>
                    <span className="pr-footer-value">{pkg.rapidReports}</span>
                  </div>
                  <div className="pr-footer-row">
                    <span className="pr-footer-label">Precise Reports</span>
                    <span className="pr-footer-value">{pkg.preciseReports}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="pr-cards-cta">
            <h3 className="pr-cta-heading">Ready to take command of your business?</h3>
            <Link className="pr-cta-btn" to="/book">
              Start Today &rarr;
            </Link>
          </div>
        </div>
      </section>

      {/* THE REAL DIFFERENCE */}
      <section className="pr-difference-section">
        <div className="pr-container">
          <div className="pr-rd-grid">
            <div>
              <div className="pr-rd-eyebrow">The Real Difference</div>
              <h2 className="pr-rd-headline">
                They manage your jobs.
                <br />
                We operate your business.
              </h2>
              <p className="pr-rd-desc">
                Every other tool in this category uses the same language: manage
                your jobs, manage your leads, manage your crews. We don't
                manage. We operate. It's a smaller word with a bigger promise
                &mdash; and it describes what roofing owners actually do every
                day.
              </p>
            </div>

            <ul className="pr-rd-list">
              {REAL_DIFFERENCE.map((item) => (
                <li className="pr-rd-item" key={item.title}>
                  <span className="pr-rd-bullet" aria-hidden="true">&bull;</span>
                  <div>
                    <strong>{item.title}</strong> {item.body}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <footer className="pr-site-footer" id="book">
        <div className="pr-container">
          <div className="pr-sf-eyebrow">Ready When You Are</div>
          <h2 className="pr-sf-headline">
            One Call.
            <br />
            <span>See the operating system live.</span>
          </h2>
          <p className="pr-sf-desc">
            No pitch deck. No pressure. We walk you through your operations,
            show you exactly how the radar coordinates, and let you decide if
            it's worth the switch.
          </p>
          <Link className="pr-sf-btn" to="/book">
            Book a Call &rarr;
          </Link>
        </div>
      </footer>

      {/* CREDITS */}
      <div className="pr-credits">
        <div className="pr-container">
          <p>&copy; {new Date().getFullYear()} RoofingAssist.AI. All rights reserved.</p>
          <div className="pr-credits-links">
            <Link to="/">Home</Link>
            <Link to="/why-we-win">Why We Win</Link>
            <Link to="/packages" className="active">Packages</Link>
          </div>
        </div>
      </div>

      {/* NOTIFICATION TOAST */}
      <div className={`pr-toast${toast ? ' show' : ''}`} aria-hidden={!toast}>
        <div className="pr-toast-icon" aria-hidden="true">&#9889;</div>
        <div className="pr-toast-content">
          <div className="pr-toast-title">{toast ? toast.title : ''}</div>
          <div className="pr-toast-desc">{toast ? toast.desc : ''}</div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
